import { AlertCircle } from "lucide-react";
import { type ComponentProps, useMemo } from "react";

import {
  useGetEncounterById,
  useGetPatientById,
} from "@elation/api-client/src/stitch-web";

import { AgendaV2, type GapListUploadV2 } from "@src/features/agenda";
import { PatientEntity } from "@src/features/patients";
import {
  useTourGuide,
  VISIT_TOUR_ENCOUNTER_ID,
  VISIT_TOUR_ID,
  VISIT_TOUR_PATIENT_ID,
  VISIT_TOUR_STEP_IDS,
  VisitTour,
} from "@src/features/tourguide";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Button } from "@src/shared/components/ui/button";
import { Skeleton } from "@src/shared/components/ui/skeleton";

import { readEncounterNoteRevision } from "../../../../application/useEncounterNoteSave";
import { EncounterEntity } from "../../../../domain";
import {
  CompletedVisit,
  type PatientSelectorPatientSummaryV2,
  PatientSelectorV2,
  useVisitTranscriptItems,
  VisitGeneratingNote,
  VisitRecordingBanner,
  type VisitRecordingV2,
  VisitStartControls,
  VisitTranscript,
} from "../../../components/v2";

export type VisitPatientSummaryV2 = PatientSelectorPatientSummaryV2;
export type { GapListUploadV2 };

export type {
  CompletedVisitMedicalCodeV2,
  VisitRecordingV2,
  VisitTranscriptItemV2,
} from "../../../components/v2";

export interface VisitPageV2Props {
  encounterId?: number;
  patientId?: number;
  patient?: VisitPatientSummaryV2 | null;
  recording?: VisitRecordingV2;
  scribingSessionId?: string | null;
  isGeneratingNote?: boolean;
  /**
   * `completedAt` baseline from the encounter actor finish request. When
   * generating, only a finish-versioned note opens the completed output.
   */
  completionBaseline?: string | null;
  isPatientSelectorOpen?: boolean;
  isLoadingEncounter?: boolean;
  isEncounterError?: boolean;
  encounterError?: unknown;
  onEncounterRetry?: () => void;
  onPatientSelectorOpenChange: (open: boolean) => void;
  onPatientSelect?: ComponentProps<typeof PatientSelectorV2>["onPatientSelect"];
  onStartVisit?: () => void;
  /**
   * Destination for the completed-visit header Back control. When the note is
   * ready the route drops its own Back chrome and this lands beside the
   * patient info instead.
   */
  backTo?: string;
}

function EncounterLoadingState() {
  return (
    <VerticalFlexbox className="mx-auto min-h-0 w-full max-w-[860px] flex-1 bg-background">
      <HorizontalFlexbox
        alignItems="center"
        className="gap-3 border-b border-border px-[18px] py-3.5"
      >
        <Skeleton className="size-8 rounded-full" />
        <VerticalFlexbox className="gap-1">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-28" />
        </VerticalFlexbox>
      </HorizontalFlexbox>
      <VerticalFlexbox className="px-[18px] py-4">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="mt-2 h-3 w-48" />
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}

function EncounterErrorState({
  error,
  onRetry,
}: {
  error?: unknown;
  onRetry?: () => void;
}) {
  return (
    <VerticalFlexbox className="mx-auto min-h-0 w-full max-w-[860px] flex-1 gap-3 bg-background p-[18px]">
      <Alert variant="destructive">
        <AlertCircle className="size-4" aria-hidden="true" />
        <AlertTitle>Unable to load visit</AlertTitle>
        <ErrorMessage
          as={AlertDescription}
          error={error ?? true}
          fallback="Something went wrong retrieving this visit."
        />
      </Alert>
      {onRetry ? (
        <Button type="button" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </VerticalFlexbox>
  );
}

interface VisitPageV2CreateEncounterProps {
  patientId?: number;
  patient?: VisitPatientSummaryV2 | null;
  isPatientSelectorOpen?: boolean;
  onPatientSelectorOpenChange: (open: boolean) => void;
  onPatientSelect?: ComponentProps<typeof PatientSelectorV2>["onPatientSelect"];
}

function VisitPageV2CreateEncounter({
  patientId,
  patient,
  isPatientSelectorOpen = false,
  onPatientSelectorOpenChange,
  onPatientSelect,
}: VisitPageV2CreateEncounterProps) {
  return (
    <VerticalFlexbox className="mx-auto min-h-0 w-full max-w-[860px] flex-1 bg-background">
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="space-between"
        className="gap-3 border-b border-border px-[18px] py-3.5"
      >
        <VisitTour.CreatePatientStep>
          <PatientSelectorV2
            patient={patient}
            open={isPatientSelectorOpen}
            canChangePatient
            onOpenChange={onPatientSelectorOpenChange}
            onPatientSelect={onPatientSelect}
          />
        </VisitTour.CreatePatientStep>
      </HorizontalFlexbox>

      <AgendaV2
        className="min-h-0 flex-1"
        encounterId={undefined}
        patientId={patientId}
      />
    </VerticalFlexbox>
  );
}

interface VisitPageV2WithEncounterProps {
  encounterId: number;
  patientId?: number;
  recording?: VisitRecordingV2;
  scribingSessionId?: string | null;
  isGeneratingNote?: boolean;
  completionBaseline?: string | null;
  isPatientSelectorOpen?: boolean;
  isLoadingEncounter?: boolean;
  isEncounterError?: boolean;
  encounterError?: unknown;
  onEncounterRetry?: () => void;
  onPatientSelectorOpenChange: (open: boolean) => void;
  onStartVisit?: () => void;
  backTo?: string;
}

function VisitPageV2WithEncounter({
  encounterId,
  patientId,
  recording,
  scribingSessionId,
  isGeneratingNote = false,
  completionBaseline = null,
  isPatientSelectorOpen = false,
  isLoadingEncounter = false,
  isEncounterError = false,
  encounterError = null,
  onEncounterRetry,
  onPatientSelectorOpenChange,
  onStartVisit,
  backTo,
}: VisitPageV2WithEncounterProps) {
  const encounter = useGetEncounterById(encounterId);
  const encounterData = encounter.data?.data;
  const encounterPatient = useGetPatientById(patientId ?? 0, {
    query: {
      enabled: patientId != null,
    },
  });
  const isLoadingPatient = patientId != null && encounterPatient.isLoading;
  const isPatientError = patientId != null && encounterPatient.isError;
  const fetchedPatient = encounterPatient.data?.data;
  const patientSummary = fetchedPatient
    ? {
        name: PatientEntity.displayName(fetchedPatient) ?? "Unknown patient",
        initials: PatientEntity.initials(fetchedPatient) ?? "?",
        metadata: PatientEntity.listMetadata(fetchedPatient),
        patientLine: PatientEntity.patientLine(fetchedPatient),
      }
    : null;
  const activeRecording = patientSummary ? recording : undefined;
  // Polls continuously (not gated on the dock's transcript panel being
  // open) so the dock strip's "Transcript (N lines)" toggle stays live even
  // while collapsed.
  const { items: transcriptItems } = useVisitTranscriptItems({
    encounterId,
    scribingSessionId,
  });
  // Memoized (matches the equivalent slot in PatientDetailPage.tsx) so an
  // unrelated re-render of this component — e.g. `isPatientSelectorOpen`
  // toggling — doesn't rebuild the transcript element and cascade into
  // `AgendaDock`/`VisitTranscript`. react-doctor's `rerender-memo-before-
  // early-return` rule flags this because it's computed before the early
  // returns below; accepted deliberately, since the early-return branches
  // (loading/error/completed-note) are comparatively rare and the memo's
  // dependencies (`activeRecording`, `transcriptItems`) already skip
  // rebuilding on every unrelated render, which matters far more here given
  // `transcriptItems` only actually changes every few seconds.
  const transcriptSlot = useMemo(
    () =>
      activeRecording ? <VisitTranscript items={transcriptItems} /> : undefined,
    [activeRecording, transcriptItems],
  );

  if (isLoadingEncounter || isLoadingPatient) {
    return <EncounterLoadingState />;
  }

  if (isEncounterError) {
    return (
      <EncounterErrorState error={encounterError} onRetry={onEncounterRetry} />
    );
  }

  if (isPatientError) {
    return <EncounterErrorState error={encounterPatient.error} />;
  }

  const noteReadyForDisplay = EncounterEntity.isNoteReadyForDisplay({
    encounter: encounterData,
    isGeneratingNote,
    completionBaseline,
  });

  if (noteReadyForDisplay) {
    const noteRevision = readEncounterNoteRevision(encounterData).noteRevision;
    return (
      <CompletedVisit
        patient={patientSummary}
        patientId={patientId}
        noteText={encounterData?.note}
        noteRevision={noteRevision}
        encounterId={encounterId}
        visitLabel={EncounterEntity.visitDateTimeLabel(encounterData)}
        scratchpadNotes={encounterData?.scratchpadNotes}
        backTo={backTo}
      />
    );
  }

  if (
    EncounterEntity.shouldShowGeneratingNote({
      encounter: encounterData,
      isEncounterFinalizing: isGeneratingNote,
      recordingIsFinishing: recording?.step === "finishing",
      completionBaseline,
    })
  ) {
    return <VisitGeneratingNote />;
  }

  return (
    <VerticalFlexbox className="mx-auto min-h-0 w-full max-w-[860px] flex-1 bg-background">
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="space-between"
        className="gap-3 border-b border-border px-[18px] py-3.5"
      >
        <PatientSelectorV2
          patient={patientSummary}
          open={isPatientSelectorOpen}
          canChangePatient={false}
          onOpenChange={onPatientSelectorOpenChange}
        />

        <VisitStartControls
          encounter={encounterData}
          encounterId={encounterId}
          patient={patientSummary}
          recording={recording}
          onStartVisit={onStartVisit}
        />
      </HorizontalFlexbox>

      {activeRecording ? (
        <div className="mx-[18px] mt-2">
          <VisitRecordingBanner recording={activeRecording} />
        </div>
      ) : null}

      <AgendaV2
        className="min-h-0 flex-1"
        encounterId={encounterId}
        patientId={patientId}
        patientFirstName={fetchedPatient?.firstName ?? undefined}
        visitStarted={EncounterEntity.isStarted(encounterData)}
        transcript={transcriptSlot}
        transcriptLineCount={
          activeRecording ? transcriptItems.length : undefined
        }
      />
    </VerticalFlexbox>
  );
}

export function VisitPageV2({
  encounterId,
  patientId,
  patient,
  recording,
  scribingSessionId,
  isGeneratingNote = false,
  completionBaseline = null,
  isPatientSelectorOpen = false,
  isLoadingEncounter = false,
  isEncounterError = false,
  encounterError = null,
  onEncounterRetry,
  onPatientSelectorOpenChange,
  onPatientSelect,
  onStartVisit,
  backTo,
}: VisitPageV2Props) {
  const visitTour = useTourGuide(VISIT_TOUR_ID);
  const shouldRenderVisitTourEncounter =
    encounterId == null &&
    visitTour.isActive &&
    visitTour.activeStepId !== VISIT_TOUR_STEP_IDS.createPatient;

  if (shouldRenderVisitTourEncounter) {
    return (
      <VisitPageV2WithEncounter
        encounterId={VISIT_TOUR_ENCOUNTER_ID}
        patientId={VISIT_TOUR_PATIENT_ID}
        recording={recording}
        scribingSessionId={scribingSessionId}
        isGeneratingNote={isGeneratingNote}
        completionBaseline={completionBaseline}
        isPatientSelectorOpen={isPatientSelectorOpen}
        isLoadingEncounter={isLoadingEncounter}
        isEncounterError={isEncounterError}
        encounterError={encounterError}
        onEncounterRetry={onEncounterRetry}
        onPatientSelectorOpenChange={onPatientSelectorOpenChange}
        onStartVisit={onStartVisit}
        backTo={backTo}
      />
    );
  }

  if (encounterId == null) {
    return (
      <VisitPageV2CreateEncounter
        patientId={patientId}
        patient={patient}
        isPatientSelectorOpen={isPatientSelectorOpen}
        onPatientSelectorOpenChange={onPatientSelectorOpenChange}
        onPatientSelect={onPatientSelect}
      />
    );
  }

  return (
    <VisitPageV2WithEncounter
      encounterId={encounterId}
      patientId={patientId}
      recording={recording}
      scribingSessionId={scribingSessionId}
      isGeneratingNote={isGeneratingNote}
      completionBaseline={completionBaseline}
      isPatientSelectorOpen={isPatientSelectorOpen}
      isLoadingEncounter={isLoadingEncounter}
      isEncounterError={isEncounterError}
      encounterError={encounterError}
      onEncounterRetry={onEncounterRetry}
      onPatientSelectorOpenChange={onPatientSelectorOpenChange}
      onStartVisit={onStartVisit}
      backTo={backTo}
    />
  );
}
